import { useQuery } from '@tanstack/react-query';
import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { RootState, AppDispatch } from '../stores/store';
import { fetchFullGuilds, clearErrors, clearError } from '../features/guilds/guildSlice';
import * as guildService from '../services/guildService';
import { getAllRulesets } from '../views/TourPage/RulesetsPage/RulesetApi';
import { GuildState } from '../models/guild';

// New hook using Redux slice
export const useGuilds = () => {
  const dispatch = useDispatch<AppDispatch>();
  const guildState = useSelector((state: RootState) => state.guilds);

  // Actions
  const actions = {
    fetchGuilds: useCallback(() => dispatch(fetchFullGuilds()), [dispatch]),
    clearErrors: useCallback(() => dispatch(clearErrors()), [dispatch]),
    clearError: useCallback(
      (errorType: keyof GuildState['error']) => dispatch(clearError(errorType)),
      [dispatch]
    ),
  };

  return {
    // State
    guilds: guildState.guilds,
    loading: guildState.loading,
    error: guildState.error,

    // Actions
    ...actions,
  };
};

// Legacy hook for backward compatibility
export const useFullGuilds = () => {
  const {
    data: guilds,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['fullGuilds'],
    queryFn: async () => {
      const result = await guildService.getAllFullGuilds();
      return result;
    },
    staleTime: 60000, // 1 minute
    gcTime: 300000, // 5 minutes
  });

  return {
    guilds: guilds || [],
    isLoading,
    error,
    refetch,
  };
};

export const useRulesets = () => {
  const {
    data: rulesets,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['rulesets'],
    queryFn: async () => {
      const result = await getAllRulesets();
      return result;
    },
    staleTime: 300000, // 5 minutes
    gcTime: 600000, // 10 minutes
  });

  return {
    rulesets: rulesets || [],
    isLoading,
    error,
    refetch,
  };
};
